import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Slider, IconButton, Switch } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import StraightenIcon from '@mui/icons-material/Straighten';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LightModeIcon from '@mui/icons-material/LightMode';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import BuildIcon from '@mui/icons-material/Build';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { useCrossSection } from './diagnosticDesign/hooks/useCrossSection';
import CrossSectionView from './diagnosticDesign/components/CrossSectionView';
import { useMarkAndDelete } from './cadFeatures/MarkAndDelete';
import { ModelBuilder } from './cadFeatures/ModelBuilder';

const t = (key, fallback) => fallback || key;

const DPR = () => Math.min(window.devicePixelRatio, 2);
const CROSS_SCALE = 0.024;
const LABEL_SCALE = 0.039;

const sxLabel = (w = 36) => ({ color: 'var(--text-3)', fontSize: 10, fontWeight: 600, width: w, flexShrink: 0 });
const sxSection = { color: 'var(--text-2)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, flex: 1 };
const sxSlider = (c) => ({ flex: 1, color: c, py: 0.5, '& .MuiSlider-thumb': { width: 10, height: 10 } });
const sxDivider = { height: '1px', bgcolor: 'var(--border)', mx: 1.5 };
const sxRow = { display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 };

async function loadGeometry(file) {
    const name = file.name.toLowerCase();
    const buf = await file.arrayBuffer();
    if (name.endsWith('.stl')) return mergeVertices(new STLLoader().parse(buf));
    if (name.endsWith('.ply')) return mergeVertices(new PLYLoader().parse(buf));
    if (name.endsWith('.obj')) {
        const obj = new OBJLoader().parse(new TextDecoder().decode(buf));
        const first = obj.children.find(c => c.isMesh);
        return first?.geometry ?? new THREE.BufferGeometry();
    }
    return new THREE.BufferGeometry();
}

function makeCrossSprite(pos, modelSize) {
    const sz = modelSize * CROSS_SCALE;
    const cv = document.createElement('canvas');
    cv.width = 32; cv.height = 32;
    const cx = cv.getContext('2d');
    cx.strokeStyle = '#ff4444'; cx.lineWidth = 2;
    cx.beginPath(); cx.moveTo(16, 4); cx.lineTo(16, 28); cx.stroke();
    cx.beginPath(); cx.moveTo(4, 16); cx.lineTo(28, 16); cx.stroke();
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(cv), depthTest: false }));
    s.position.copy(pos); s.scale.set(sz, sz, 1); s.renderOrder = 999;
    return s;
}

function makeLabelSprite(pos, dist, modelSize) {
    const cv = document.createElement('canvas');
    cv.width = 192; cv.height = 64;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = 'rgba(0,0,0,0.75)';
    if (ctx.roundRect) { ctx.roundRect(0, 0, 192, 64, 10); ctx.fill(); }
    else { ctx.fillRect(0, 0, 192, 64); }
    ctx.fillStyle = '#fff'; ctx.font = 'bold 28px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(`${dist.toFixed(2)} mm`, 96, 42);
    const sc = modelSize * LABEL_SCALE;
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(cv), depthTest: false }));
    s.position.copy(pos); s.scale.set(sc * 3, sc, 1); s.renderOrder = 1000;
    return s;
}

const STLFullViewer = ({ upper, lower, singleFile, onScreenshot }) => {
    const viewportRef = useRef(null);
    const mountRef = useRef(null);

    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const rendererRef = useRef(null);
    const meshEntriesRef = useRef([]);
    const groupRef = useRef(null);
    const initCamRef = useRef({ pos: new THREE.Vector3(), zoom: 1 });
    const modelSizeRef = useRef(1);

    const objectsRef = useRef([]);
    const pivotRef = useRef(new THREE.Object3D());
    const maxillaMeshesRef = useRef([]);
    const mandibleMeshesRef = useRef([]);
    const csOutlineRendererRef = useRef(null);

    const cs = useCrossSection({ sceneRef, objectsRef, pivotRef, maxillaMeshesRef, mandibleMeshesRef }, t);

    const measurePointsRef = useRef([]);
    const measureMarkersRef = useRef(new THREE.Group());
    const pendingCrossRef = useRef(null);
    const raycasterRef = useRef(new THREE.Raycaster());
    const measureIdRef = useRef(0);

    const keyLightRef = useRef(null);
    const fillLightRef = useRef(null);
    const rimLightRef = useRef(null);
    const hemiLightRef = useRef(null);

    const [meshStates, setMeshStates] = useState([]);
    const [toolMode, setToolMode] = useState('orbit');
    const [measurements, setMeasurements] = useState([]);
    const [lightCfg, setLightCfg] = useState({ key: 1.6, fill: 0.5, rim: 1.0, ambient: 0.9, exposure: 0.7 });

    const [selectedMeshIdx, setSelectedMeshIdx] = useState(0);
    const [modelBuilderOpen, setModelBuilderOpen] = useState(false);
    const [modelBuilderResult, setModelBuilderResult] = useState(null);
    const [, setGeoVersion] = useState(0);

    const _dGeo = useRef(new THREE.BufferGeometry());
    const _dMesh = useRef(new THREE.Mesh());
    const _dScene = useRef(new THREE.Scene());
    const _dCam = useRef(new THREE.OrthographicCamera());
    const _dCanvas = useRef(document.createElement('canvas'));

    const selectedEntry = meshEntriesRef.current[selectedMeshIdx];

    const handleGeometryChange = useCallback((newGeo) => {
        const entry = meshEntriesRef.current[selectedMeshIdx];
        if (!entry) return;
        entry.mesh.geometry.dispose();
        entry.mesh.geometry = newGeo;
        setGeoVersion(v => v + 1);
    }, [selectedMeshIdx]);

    const markDelete = useMarkAndDelete({
        geometry: selectedEntry?.mesh?.geometry ?? _dGeo.current,
        mesh: selectedEntry?.mesh ?? _dMesh.current,
        scene: sceneRef.current ?? _dScene.current,
        camera: (cameraRef.current ?? _dCam.current),
        renderer: (rendererRef.current ?? { domElement: _dCanvas.current }),
        controls: controlsRef.current,
        onGeometryChange: handleGeometryChange,
        active: toolMode === 'markDelete',
    });

    const handleModelBuilderResult = useCallback((resultGeo) => {
        const entry = meshEntriesRef.current[selectedMeshIdx];
        if (!entry) return;
        entry.mesh.geometry.dispose();
        entry.mesh.geometry = resultGeo;
        setModelBuilderResult(resultGeo);
        setGeoVersion(v => v + 1);
    }, [selectedMeshIdx]);

    const selectMesh = useCallback((i) => {
        if (i === selectedMeshIdx) return;
        setSelectedMeshIdx(i);
        setModelBuilderResult(null);
        setToolMode('orbit');
        if (controlsRef.current) controlsRef.current.enabled = true;
    }, [selectedMeshIdx]);

    useEffect(() => {
        const el = mountRef.current;
        if (!el) return;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color('#12122a');
        sceneRef.current = scene;

        const w = el.clientWidth, h = el.clientHeight, aspect = w / h;
        const camera = new THREE.OrthographicCamera(-aspect, aspect, 1, -1, 0.1, 10000);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
        renderer.setSize(w, h);
        renderer.setPixelRatio(DPR());
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 0.7;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.localClippingEnabled = true;
        el.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        const pmrem = new THREE.PMREMGenerator(renderer);
        scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
        pmrem.dispose();

        const hemiL = new THREE.HemisphereLight('#d4e4ff', '#a09070', 0.9); scene.add(hemiL); hemiLightRef.current = hemiL;
        const keyL = new THREE.DirectionalLight('#fff5e6', 1.6); keyL.position.set(3, 4, 6); scene.add(keyL); keyLightRef.current = keyL;
        const fillL = new THREE.DirectionalLight('#c8d8f0', 0.5); fillL.position.set(-5, 2, -1); scene.add(fillL); fillLightRef.current = fillL;
        const rimL = new THREE.DirectionalLight('#e0ecff', 1.0); rimL.position.set(0, -2, -6); scene.add(rimL); rimLightRef.current = rimL;

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true; controls.dampingFactor = 0.06; controls.enableZoom = true;
        controls.minPolarAngle = -Infinity;
        controls.maxPolarAngle = Infinity;
        const sph = controls._spherical;
        if (sph) sph.makeSafe = () => sph;
        controlsRef.current = controls;

        const group = new THREE.Group();
        scene.add(group); groupRef.current = group;
        scene.add(measureMarkersRef.current);

        (async () => {
            const files = [];
            if (singleFile) files.push({ file: singleFile, label: singleFile.name.replace(/\.[^.]+$/, ''), color: '#d4c4a0', isUpper: true });
            else {
                if (upper) files.push({ file: upper, label: 'Maxilla', color: '#d4c4a0', isUpper: true });
                if (lower) files.push({ file: lower, label: 'Mandible', color: '#b0a090', isUpper: false });
            }

            const entries = [], states = [];
            const upMeshes = [], loMeshes = [];

            for (const { file, label, color, isUpper } of files) {
                const geo = await loadGeometry(file); geo.computeVertexNormals();
                const mat = new THREE.MeshPhysicalMaterial({
                    color: new THREE.Color(color), roughness: 0.7, metalness: 0,
                    clearcoat: 0.04, side: THREE.DoubleSide, envMapIntensity: 1.2,
                    transparent: true, opacity: 1,
                });
                const mesh = new THREE.Mesh(geo, mat);
                group.add(mesh);
                entries.push({ label, color, mesh, material: mat });
                states.push({ label, color, visible: true, opacity: 1 });
                (isUpper ? upMeshes : loMeshes).push(mesh);
            }

            meshEntriesRef.current = entries; setMeshStates(states);
            objectsRef.current = [group]; maxillaMeshesRef.current = upMeshes; mandibleMeshesRef.current = loMeshes;

            const box = new THREE.Box3().setFromObject(group);
            group.position.sub(box.getCenter(new THREE.Vector3()));
            const size = box.getSize(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);
            modelSizeRef.current = maxDim;
            const half = maxDim * 0.6;
            camera.left = -half * aspect; camera.right = half * aspect; camera.top = half; camera.bottom = -half;
            camera.updateProjectionMatrix();
            camera.position.set(0, half * 0.5, half * 2.5);
            camera.lookAt(0, 0, 0); controls.target.set(0, 0, 0); controls.update();
            initCamRef.current = { pos: camera.position.clone(), zoom: camera.zoom };
        })();

        const onResize = () => {
            const rw = el.clientWidth, rh = el.clientHeight, a = rw / rh;
            const halfH = (camera.top - camera.bottom) / 2;
            camera.left = -halfH * a; camera.right = halfH * a; camera.updateProjectionMatrix();
            renderer.setSize(rw, rh);
        };
        window.addEventListener('resize', onResize);

        let animId;
        const animate = () => {
            animId = requestAnimationFrame(animate);
            controls.update(); renderer.render(scene, camera);
            try {
                const csCanvas = cs.csOutlineCanvasRef.current;
                if (csCanvas && cs.csOutlineLinesRef.current) {
                    if (!csOutlineRendererRef.current) {
                        const r = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
                        r.toneMapping = THREE.NoToneMapping; r.setClearColor(0x1a1a1a); r.setPixelRatio(1);
                        csOutlineRendererRef.current = r;
                    }
                    const r = csOutlineRendererRef.current, cw = csCanvas.clientWidth, ch = csCanvas.clientHeight;
                    if (cw > 0 && ch > 0) {
                        const cam2 = cs.csOutlineCameraRef.current;
                        const hh = Math.abs(cam2.top - cam2.bottom) / 2 || 1, a2 = cw / ch;
                        cam2.left = -hh * a2; cam2.right = hh * a2; cam2.updateProjectionMatrix();
                        const dpr = DPR(), pw = Math.round(cw * dpr), ph = Math.round(ch * dpr);
                        r.setSize(pw, ph, false); r.render(cs.csOutlineSceneRef.current, cam2);
                        if (csCanvas.width !== pw || csCanvas.height !== ph) { csCanvas.width = pw; csCanvas.height = ph; }
                        const ctx = csCanvas.getContext('2d');
                        if (ctx) ctx.drawImage(r.domElement, 0, 0);
                    }
                }
            } catch { /* skip */ }
        };
        animate();

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener('resize', onResize);
            controls.dispose(); renderer.dispose();
            if (csOutlineRendererRef.current) { csOutlineRendererRef.current.dispose(); csOutlineRendererRef.current = null; }
            if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
        };
    }, [upper, lower, singleFile]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        meshEntriesRef.current.forEach((entry, i) => {
            const s = meshStates[i]; if (!s) return;
            entry.mesh.visible = s.visible;
            entry.material.opacity = s.opacity;
            entry.material.transparent = s.opacity < 1;
            entry.material.depthWrite = s.opacity >= 0.95;
            entry.material.needsUpdate = true;
        });
    }, [meshStates]);

    useEffect(() => {
        if (keyLightRef.current) keyLightRef.current.intensity = lightCfg.key;
        if (fillLightRef.current) fillLightRef.current.intensity = lightCfg.fill;
        if (rimLightRef.current) rimLightRef.current.intensity = lightCfg.rim;
        if (hemiLightRef.current) hemiLightRef.current.intensity = lightCfg.ambient;
        if (rendererRef.current) rendererRef.current.toneMappingExposure = lightCfg.exposure;
    }, [lightCfg]);

    const handleCanvasClick = useCallback((e) => {
        if (toolMode !== 'measure') return;
        const renderer = rendererRef.current, camera = cameraRef.current, group = groupRef.current;
        if (!renderer || !camera || !group) return;

        const rect = renderer.domElement.getBoundingClientRect();
        const mouse = new THREE.Vector2(
            ((e.clientX - rect.left) / rect.width) * 2 - 1,
            -((e.clientY - rect.top) / rect.height) * 2 + 1,
        );
        raycasterRef.current.setFromCamera(mouse, camera);
        const hits = raycasterRef.current.intersectObjects(group.children, true);
        if (hits.length === 0) return;

        const point = hits[0].point.clone();
        measurePointsRef.current.push(point);
        const mSize = modelSizeRef.current;

        if (measurePointsRef.current.length === 1) {
            if (pendingCrossRef.current) measureMarkersRef.current.remove(pendingCrossRef.current);
            const cross = makeCrossSprite(point, mSize);
            measureMarkersRef.current.add(cross);
            pendingCrossRef.current = cross;
        }

        if (measurePointsRef.current.length === 2) {
            if (pendingCrossRef.current) { measureMarkersRef.current.remove(pendingCrossRef.current); pendingCrossRef.current = null; }
            const [p1, p2] = measurePointsRef.current;
            const dist = p1.distanceTo(p2);
            const mid = p1.clone().add(p2).multiplyScalar(0.5);

            const mGroup = new THREE.Group();
            mGroup.add(makeCrossSprite(p1, mSize), makeCrossSprite(p2, mSize));
            const line = new THREE.Line(
                new THREE.BufferGeometry().setFromPoints([p1, p2]),
                new THREE.LineBasicMaterial({ color: 0xff4444, depthTest: false }),
            );
            line.renderOrder = 999; mGroup.add(line);
            mGroup.add(makeLabelSprite(mid, dist, mSize));

            measureMarkersRef.current.add(mGroup);
            setMeasurements(prev => [...prev, { dist, id: ++measureIdRef.current, group: mGroup }]);
            measurePointsRef.current = [];
        }
    }, [toolMode]);

    const toggleVisibility = (i) => setMeshStates(p => p.map((s, idx) => idx === i ? { ...s, visible: !s.visible } : s));
    const setOpacity = (i, v) => setMeshStates(p => p.map((s, idx) => idx === i ? { ...s, opacity: v } : s));

    const clearMeasurements = () => {
        const g = measureMarkersRef.current;
        while (g.children.length) g.remove(g.children[0]);
        pendingCrossRef.current = null; measurePointsRef.current = [];
        setMeasurements([]);
    };

    const removeMeasurement = (id) => {
        setMeasurements(prev => {
            const m = prev.find(x => x.id === id);
            if (m) { measureMarkersRef.current.remove(m.group); m.group.traverse(c => { if (c.geometry) c.geometry.dispose(); }); }
            return prev.filter(x => x.id !== id);
        });
    };

    const handleReset = () => {
        const cam = cameraRef.current, ctrl = controlsRef.current;
        if (cam && ctrl) { cam.position.copy(initCamRef.current.pos); cam.zoom = initCamRef.current.zoom; cam.updateProjectionMatrix(); ctrl.target.set(0, 0, 0); ctrl.update(); }
        setMeshStates(p => p.map(s => ({ ...s, visible: true, opacity: 1 })));
        cs.setCrossSectionEnabled(false); clearMeasurements(); setToolMode('orbit');
        setModelBuilderOpen(false); setModelBuilderResult(null);
        if (controlsRef.current) controlsRef.current.enabled = true;
    };

    const toggleMeasure = () => {
        const next = toolMode === 'measure' ? 'orbit' : 'measure';
        setToolMode(next);
        if (controlsRef.current) controlsRef.current.enabled = next !== 'measure';
    };

    const toggleMarkDelete = () => {
        const next = toolMode === 'markDelete' ? 'orbit' : 'markDelete';
        setToolMode(next);
        if (next !== 'markDelete' && controlsRef.current) controlsRef.current.enabled = true;
        if (next === 'markDelete') clearMeasurements();
    };

    const takeScreenshot = useCallback(() => {
        const r = rendererRef.current, s = sceneRef.current, cam = cameraRef.current;
        if (!r || !s || !cam || !onScreenshot) return;
        r.render(s, cam);
        onScreenshot(r.domElement.toDataURL('image/png'));
    }, [onScreenshot]);

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
            <Box ref={viewportRef} sx={{ flex: 1, minWidth: 0, height: '100%', position: 'relative', overflow: 'hidden' }}>
                <Box ref={mountRef}
                    onClick={handleCanvasClick}
                    onPointerDown={markDelete.handlePointerDown}
                    onPointerMove={markDelete.handlePointerMove}
                    onPointerUp={markDelete.handlePointerUp}
                    onContextMenu={markDelete.handleContextMenu}
                    sx={{ position: 'absolute', inset: 0, cursor: toolMode === 'markDelete' || toolMode === 'measure' ? 'crosshair' : 'grab' }} />
                <CrossSectionView
                    t={t} crossSectionEnabled={cs.crossSectionEnabled}
                    crossSectionFullscreen={cs.crossSectionFullscreen} setCrossSectionFullscreen={cs.setCrossSectionFullscreen}
                    crossSectionAxis={cs.crossSectionAxis} setCrossSectionAxis={cs.setCrossSectionAxis}
                    crossSectionPosition={cs.crossSectionPosition} setCrossSectionPosition={cs.setCrossSectionPosition}
                    crossSectionAngle={cs.crossSectionAngle} setCrossSectionAngle={cs.setCrossSectionAngle}
                    crossSectionPlaneOpacity={cs.crossSectionPlaneOpacity} setCrossSectionPlaneOpacity={cs.setCrossSectionPlaneOpacity}
                    maxillaFile={upper || singleFile || null} mandibleFile={lower || null}
                    csMeasureMode={cs.csMeasureMode} setCsMeasureMode={cs.setCsMeasureMode}
                    csMeasurements={cs.csMeasurements} setCsMeasurements={cs.setCsMeasurements}
                    csMeasurePending={cs.csMeasurePending} setCsMeasurePending={cs.setCsMeasurePending}
                    csMeasureOverlays={cs.csMeasureOverlays}
                    crossSectionCanvasRef={cs.crossSectionCanvasRef}
                    csOutlineCanvasRef={cs.csOutlineCanvasRef}
                    csOutlineCameraRef={cs.csOutlineCameraRef}
                />
            </Box>

            {meshStates.length > 0 && (
            <Box sx={{
                width: 200, flexShrink: 0, height: '100%', overflowY: 'auto',
                bgcolor: 'var(--bg-surface)', borderLeft: '1px solid var(--border)',
                display: 'flex', flexDirection: 'column',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'var(--border-h)', borderRadius: 2 },
            }}>
                {/* Models */}
                <Box sx={{ px: 1.5, pt: 2, pb: 1.5 }}>
                    <Box sx={{ ...sxSection, mb: 1.5 }}>Models</Box>
                    {meshStates.map((s, i) => (
                        <Box key={s.label} sx={{ mb: 2, cursor: meshStates.length > 1 ? 'pointer' : 'default' }}
                            onClick={() => meshStates.length > 1 && selectMesh(i)}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75,
                                ...(meshStates.length > 1 && i === selectedMeshIdx && {
                                    bgcolor: 'rgba(123,104,238,0.08)', mx: -0.75, px: 0.75, py: 0.25,
                                    borderRadius: 1, border: '1px solid rgba(123,104,238,0.2)',
                                }),
                            }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.color, flexShrink: 0, border: '1px solid var(--border)' }} />
                                <Box sx={{ color: 'var(--text-1)', fontSize: 13, flex: 1, fontWeight: 500 }}>{s.label}</Box>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleVisibility(i); }}
                                    sx={{ color: s.visible ? 'var(--text-2)' : 'var(--text-3)', p: 0.3, '&:hover': { color: 'var(--text-1)' } }}>
                                    {s.visible ? <VisibilityIcon sx={{ fontSize: 18 }} /> : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2.5 }} onClick={e => e.stopPropagation()}>
                                <Box sx={sxLabel(48)}>Opacity</Box>
                                <Slider size="small" min={0} max={1} step={0.01} value={s.opacity}
                                    onChange={(_, v) => setOpacity(i, v)} disabled={!s.visible}
                                    sx={{ ...sxSlider('#7b68ee'), '& .MuiSlider-thumb': { width: 12, height: 12 } }} />
                                <Box sx={{ ...sxLabel(32), textAlign: 'right' }}>{Math.round(s.opacity * 100)}%</Box>
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Box sx={sxDivider} />

                {/* Cross Section */}
                <Box sx={{ px: 1.5, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: cs.crossSectionEnabled ? 1.5 : 0 }}>
                        <ContentCutIcon sx={{ fontSize: 18, color: cs.crossSectionEnabled ? '#7ecbf5' : 'rgba(255,255,255,0.3)' }} />
                        <Box sx={sxSection}>Cross Section</Box>
                        <Switch size="small" checked={cs.crossSectionEnabled} onChange={(_, v) => cs.setCrossSectionEnabled(v)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#7ecbf5' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#7ecbf5' } }} />
                    </Box>
                    {cs.crossSectionEnabled && (<>
                        <Box sx={{ display: 'flex', gap: '4px', mb: 1.5 }}>
                            {['y', 'x', 'z'].map(a => (
                                <Box key={a} onClick={() => { cs.setCrossSectionAxis(a); cs.setCrossSectionPosition(0); cs.setCrossSectionAngle(0); }}
                                    sx={{
                                        flex: 1, textAlign: 'center', py: 0.5, borderRadius: 1, fontSize: 10, cursor: 'pointer', fontWeight: 600,
                                        bgcolor: cs.crossSectionAxis === a ? 'rgba(126,203,245,0.2)' : 'var(--bg-input)',
                                        color: cs.crossSectionAxis === a ? '#7ecbf5' : 'var(--text-3)',
                                        border: `1px solid ${cs.crossSectionAxis === a ? 'rgba(126,203,245,0.3)' : 'var(--border)'}`,
                                    }}>
                                    {a === 'y' ? 'Horiz' : a === 'x' ? 'Sagit' : 'Coron'}
                                </Box>
                            ))}
                        </Box>
                        <Box sx={sxRow}><Box sx={sxLabel()}>Pos</Box>
                            <Slider size="small" min={-1} max={1} step={0.01} value={cs.crossSectionPosition}
                                onChange={(_, v) => cs.setCrossSectionPosition(v)} sx={sxSlider('#7ecbf5')} /></Box>
                        <Box sx={sxRow}><Box sx={sxLabel()}>Rot</Box>
                            <Slider size="small" min={-90} max={90} step={1} value={cs.crossSectionAngle}
                                onChange={(_, v) => cs.setCrossSectionAngle(v)} sx={sxSlider('#f5c77e')} /></Box>
                        <Box sx={{ ...sxRow, mb: 0 }}><Box sx={sxLabel()}>Plane</Box>
                            <Slider size="small" min={0} max={1} step={0.01} value={cs.crossSectionPlaneOpacity}
                                onChange={(_, v) => cs.setCrossSectionPlaneOpacity(v)} sx={sxSlider('#a5f57e')} /></Box>
                    </>)}
                </Box>

                <Box sx={sxDivider} />

                {/* Measure */}
                <Box sx={{ px: 1.5, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <StraightenIcon sx={{ fontSize: 18, color: toolMode === 'measure' ? '#ff4444' : 'rgba(255,255,255,0.3)' }} />
                        <Box sx={sxSection}>Measure</Box>
                        <Box onClick={toggleMeasure} sx={{
                            px: 1.2, py: 0.4, borderRadius: 1, fontSize: 11, cursor: 'pointer', fontWeight: 600,
                            bgcolor: toolMode === 'measure' ? '#ff4444' : 'var(--bg-input)',
                            color: toolMode === 'measure' ? '#fff' : 'var(--text-2)',
                            border: `1px solid ${toolMode === 'measure' ? '#ff4444' : 'var(--border)'}`,
                            '&:hover': { borderColor: '#ff4444' },
                        }}>{toolMode === 'measure' ? 'Active' : 'Start'}</Box>
                    </Box>
                    {toolMode === 'measure' && <Box sx={{ color: 'var(--text-3)', fontSize: 11, pl: 0.5, mb: 0.75 }}>Click two points on the model</Box>}
                    {measurements.length > 0 && (
                        <Box sx={{ mt: 0.75 }}>
                            {measurements.map((m, i) => (
                                <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff4444', flexShrink: 0 }} />
                                    <Box sx={{ color: 'var(--text-2)', fontSize: 12, flex: 1 }}>#{i + 1}: <strong>{m.dist.toFixed(2)} mm</strong></Box>
                                    <IconButton size="small" onClick={() => removeMeasurement(m.id)}
                                        sx={{ p: 0.2, color: 'var(--text-3)', '&:hover': { color: '#ff4444' } }}>
                                        <CloseIcon sx={{ fontSize: 12 }} />
                                    </IconButton>
                                </Box>
                            ))}
                            <Box onClick={clearMeasurements} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, cursor: 'pointer', '&:hover': { color: '#ff4444' } }}>
                                <DeleteIcon sx={{ fontSize: 16, color: 'var(--text-3)' }} />
                                <Box sx={{ color: 'var(--text-3)', fontSize: 11 }}>Clear all</Box>
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box sx={sxDivider} />

                {/* Mark & Delete */}
                <Box sx={{ px: 1.5, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: toolMode === 'markDelete' ? 1 : 0 }}>
                        <CleaningServicesIcon sx={{ fontSize: 18, color: toolMode === 'markDelete' ? '#ff9100' : 'rgba(255,255,255,0.3)' }} />
                        <Box sx={sxSection}>Mark & Delete</Box>
                        <Box onClick={toggleMarkDelete} sx={{
                            px: 1.2, py: 0.4, borderRadius: 1, fontSize: 11, cursor: 'pointer', fontWeight: 600,
                            bgcolor: toolMode === 'markDelete' ? '#ff9100' : 'var(--bg-input)',
                            color: toolMode === 'markDelete' ? '#fff' : 'var(--text-2)',
                            border: `1px solid ${toolMode === 'markDelete' ? '#ff9100' : 'var(--border)'}`,
                            '&:hover': { borderColor: '#ff9100' },
                        }}>{toolMode === 'markDelete' ? 'Active' : 'Start'}</Box>
                    </Box>
                    {toolMode === 'markDelete' && (
                        <Box>
                            <markDelete.Panel />
                        </Box>
                    )}
                </Box>

                <Box sx={sxDivider} />

                {/* Model Builder */}
                <Box sx={{ px: 1.5, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: modelBuilderOpen ? 1 : 0 }}>
                        <BuildIcon sx={{ fontSize: 18, color: modelBuilderOpen ? '#ef5350' : 'rgba(255,255,255,0.3)' }} />
                        <Box sx={sxSection}>Model Builder</Box>
                        <Switch size="small" checked={modelBuilderOpen} onChange={(_, v) => setModelBuilderOpen(v)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ef5350' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ef5350' } }} />
                    </Box>
                    {modelBuilderOpen && selectedEntry && (
                        <ModelBuilder
                            geometry={selectedEntry.mesh.geometry}
                            onResult={handleModelBuilderResult}
                            resultGeometry={modelBuilderResult}
                            accentColor="#ef5350"
                        />
                    )}
                </Box>

                <Box sx={sxDivider} />

                {/* Lighting */}
                <Box sx={{ px: 1.5, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                        <LightModeIcon sx={{ fontSize: 18, color: '#f5c77e' }} />
                        <Box sx={sxSection}>Lighting</Box>
                    </Box>
                    <Box sx={sxRow}><Box sx={sxLabel(48)}>Key</Box>
                        <Slider size="small" min={0} max={4} step={0.1} value={lightCfg.key}
                            onChange={(_, v) => setLightCfg(p => ({ ...p, key: v }))} sx={sxSlider('#f5c77e')} />
                    </Box>
                    <Box sx={sxRow}><Box sx={sxLabel(48)}>Fill</Box>
                        <Slider size="small" min={0} max={3} step={0.1} value={lightCfg.fill}
                            onChange={(_, v) => setLightCfg(p => ({ ...p, fill: v }))} sx={sxSlider('#c8d8f0')} />
                    </Box>
                    <Box sx={sxRow}><Box sx={sxLabel(48)}>Rim</Box>
                        <Slider size="small" min={0} max={3} step={0.1} value={lightCfg.rim}
                            onChange={(_, v) => setLightCfg(p => ({ ...p, rim: v }))} sx={sxSlider('#e0ecff')} />
                    </Box>
                    <Box sx={sxRow}><Box sx={sxLabel(48)}>Ambient</Box>
                        <Slider size="small" min={0} max={3} step={0.1} value={lightCfg.ambient}
                            onChange={(_, v) => setLightCfg(p => ({ ...p, ambient: v }))} sx={sxSlider('#a5f57e')} />
                    </Box>
                    <Box sx={{ ...sxRow, mb: 0 }}><Box sx={sxLabel(48)}>Expose</Box>
                        <Slider size="small" min={0.1} max={2} step={0.05} value={lightCfg.exposure}
                            onChange={(_, v) => setLightCfg(p => ({ ...p, exposure: v }))} sx={sxSlider('#f5a07e')} />
                    </Box>
                </Box>

                <Box sx={{ flex: 1 }} />

                {/* Bottom buttons */}
                <Box sx={{ px: 1.5, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {onScreenshot && (
                        <Box onClick={takeScreenshot} sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                            py: 0.75, borderRadius: 1, cursor: 'pointer',
                            bgcolor: 'rgba(123,104,238,0.15)', border: '1px solid rgba(123,104,238,0.3)',
                            '&:hover': { bgcolor: 'rgba(123,104,238,0.25)', borderColor: '#7b68ee' },
                        }}>
                            <CameraAltIcon sx={{ fontSize: 18, color: '#7b68ee' }} />
                            <Box sx={{ color: 'var(--text-2)', fontSize: 12, fontWeight: 600 }}>Screenshot</Box>
                        </Box>
                    )}
                    <Box onClick={handleReset} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                        py: 0.75, borderRadius: 1, cursor: 'pointer',
                        bgcolor: 'var(--bg-input)', border: '1px solid var(--border)',
                        '&:hover': { borderColor: '#7b68ee', bgcolor: 'rgba(123,104,238,0.1)' },
                    }}>
                        <RestartAltIcon sx={{ fontSize: 18, color: 'var(--text-3)' }} />
                        <Box sx={{ color: 'var(--text-2)', fontSize: 12, fontWeight: 600 }}>Reset View</Box>
                    </Box>
                </Box>
            </Box>)}
        </Box>
    );
};

export default STLFullViewer;
